/**
 * What a service probe saw, in the two parts the panel shows.
 *
 * A probe knows its own facts: a balance, a bucket, a verified number, so it
 * says which one is the verdict and which are the particulars. Nothing
 * downstream has to take a sentence apart: the panel that renders this would
 * have to guess where a verdict ends, and it would guess wrong the first time a
 * provider worded something differently.
 */
export interface ProbeEvidence {
  /** The verdict, read at a glance: "Authenticated", "$29.93 balance". */
  headline: string;
  /** Its particulars, or nothing: "as ops@snily.io via smtp.example.com". */
  detail: string | null;
  /**
   * Named checks that belong beside the verdict rather than inside it.
   *
   * A sentence like "SPF ✓ DKIM ✗ DMARC ✓" hides the one that failed among the
   * ones that did not: it has to be read to be understood. As three named
   * cells, always in the same order, position does the work and the failing one
   * is red before a word is read.
   */
  checks?: ProbeCheck[];
  /**
   * What the probe actually read, for whoever is fixing it. Folded away behind
   * a mark: it answers "what is published right now", which otherwise means
   * leaving the panel and running `dig`.
   */
  records?: ProbeRecord[];
}

export interface ProbeCheck {
  /** Short name of the check: SPF, DKIM, DMARC. */
  label: string;
  /** What it found, in a word or two: "pass", "privateemail", "missing". */
  value: string;
  ok: boolean;
}

export interface ProbeRecord {
  label: string;
  value: string;
}

/**
 * Builds evidence from a verdict and any number of supporting facts. Empty
 * facts drop out, so a probe can offer what it has without composing strings
 * around the parts it does not.
 */
export function evidence(
  headline: string,
  ...facts: (string | null | undefined | false)[]
): ProbeEvidence {
  const detail = facts.filter((fact): fact is string => Boolean(fact)).join(', ');
  return { headline, detail: detail || null };
}

/** The same verdict, with named checks and the raw records behind them. */
export function evidenceWithChecks(
  headline: string,
  detail: string | null,
  checks: ProbeCheck[],
  records: ProbeRecord[],
): ProbeEvidence {
  return { headline, detail, checks, records };
}

/**
 * A failure the probe recognised, carrying the same two parts.
 *
 * Anything else a provider throws: an SDK error, a socket reset, arrives as a
 * plain Error and is shown whole. That is the one case nobody can structure in
 * advance, and it is better read in full than cut at a guessed boundary.
 */
export class ProbeFailure extends Error {
  constructor(
    readonly headline: string,
    readonly facts: string | null = null,
  ) {
    super(facts ? `${headline} ${facts}` : headline);
    this.name = 'ProbeFailure';
  }
}
